import { type ReactNode, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

import { useI18n } from '../i18n/I18n.js';
import { Notice } from '../ui/parts.js';
import { useAuth } from './AuthProvider.js';
import type { SignInState } from './session.js';

/** Only a path on this console: a `returnTo` naming another origin is ignored. */
function safeReturn(state: unknown): string {
  const to = (state as Partial<SignInState> | undefined)?.returnTo;
  return typeof to === 'string' && to.startsWith('/') && !to.startsWith('//') ? to : '/';
}

/** `/auth/callback`: finishes the code exchange, then lands where the person was going. */
export function Callback() {
  const { manager } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice; a code can be exchanged once.
    if (done.current) return;
    done.current = true;
    manager
      .signinRedirectCallback()
      .then((user) => navigate(safeReturn(user.state), { replace: true }))
      .catch(() => navigate('/sign-in-failed', { replace: true }));
  }, [manager, navigate]);

  return <p className="visually-hidden">{t.starting}</p>;
}

/** Everything behind sign-in. Signed out, the browser goes straight to Zitadel's page. */
export function RequireSignIn({ children }: { children: ReactNode }) {
  const { user, signIn } = useAuth();
  const { t } = useI18n();
  const started = useRef(false);

  useEffect(() => {
    if (user === null && !started.current) {
      started.current = true;
      void signIn(`${window.location.pathname}${window.location.search}`);
    }
  }, [user, signIn]);

  if (user === undefined || user === null) {
    return <p className="visually-hidden">{t.starting}</p>;
  }
  return <>{children}</>;
}

function SignInAgain() {
  const { signIn } = useAuth();
  const { t } = useI18n();
  return (
    <button type="button" className="button button-primary" onClick={() => void signIn('/')}>
      {t.signInAgain}
    </button>
  );
}

export function SignedOut() {
  const { t } = useI18n();
  return (
    <Notice title={t.signedOut.title} action={<SignInAgain />}>
      <p className="notice-text">{t.signedOut.body}</p>
    </Notice>
  );
}

export function SignInFailed() {
  const { t } = useI18n();
  return (
    <Notice title={t.signInFailed.title} action={<SignInAgain />}>
      <p className="notice-text">{t.signInFailed.body}</p>
    </Notice>
  );
}

/** Signed in, but no school where this person is an owner or a manager. */
export function NoAccess() {
  const { t } = useI18n();
  const { signOut } = useAuth();
  return (
    <Notice
      title={t.noAccess.title}
      action={
        <button type="button" className="button button-quiet" onClick={() => void signOut()}>
          {t.signOut}
        </button>
      }
    >
      <p className="notice-text">{t.noAccess.body}</p>
    </Notice>
  );
}
